import React, { ChangeEvent } from 'react';
import { memo, useState, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  changeCompanyDeviceAtom,
  changeWeathermodeAtom,
} from '../../../models/atoms/company_atoms';
import { company_controller } from '../../../controllers/company_controller';
import { useTranslation } from '../../../contexts/TranslationContext';
import { canManageDevicesSelector } from '../../../models/selectors/permission_selectors';
import { MAX_MEASUREMENTS } from './DeviceSettings';
import { Stack, FormControlLabel, Switch, InputAdornment } from '@mui/material';
import { CheckPermission } from '../CheckPermission';
import { MHMButton } from '../MHM/MHMButton';
import { MHMTextField } from '../MHM/MHMTextField';
import { MHMTypography, DisclaimerText, ErrorText } from '../MHM/MHMTypography';
import { ConfirmSaveModal } from './ConfirmSaveModal';
import { FrequenciesSettings } from './FrequenciesSettings';
import { SettingsCard } from './SettingsCard';
import { ChangedFrequency } from './ChangedFrequency';
import { useIsMobile } from '../../../hooks/useIsMobile';
/* eslint-disable no-useless-escape */

export const WeatherModeSettingsComponent = memo(
  function WeatherModeSettingsComponent(
    _: React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >,
  ) {
    const { t, distance } = useTranslation();
    const isMobile = useIsMobile();
    const times = useRecoilValue(changeCompanyDeviceAtom);
    const [weathermodeSettings, setWeathermodeSettings] = useRecoilState(
      changeWeathermodeAtom,
    );
    const canManageDevices = useRecoilValue(canManageDevicesSelector);
    const [weathermodeRelayError, setWeathermodeRelayError] = useState<
      [string, { minHours: string; maxHours: string }]
    >(['', { minHours: '.2', maxHours: '' }]);
    const [weathermodeReadingError, setWeathermodeReadingError] = useState<
      [string, { minReadingFrequency: string; maxReadingFrequency: string }]
    >(['', { minReadingFrequency: '', maxReadingFrequency: '' }]);
    const [rainfallThresholdError, setRainfallThresholdError] = useState<
      [string, { minMillimeters: string; maxMillimeters: string }]
    >(['', { minMillimeters: '', maxMillimeters: '' }]);
    const [
      cumulativeRainfallThresholdError,
      setCumulativeRainfallThresholdError,
    ] = useState<[string, { minMillimeters: string; maxMillimeters: string }]>([
      '',
      { minMillimeters: '', maxMillimeters: '' },
    ]);
    const [totalMeasurementsWarning, setTotalMeasurementsWarning] =
      useState('');
    const [modal, setModal] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0);
    function openModal() {
      setModal(true);
    }
    function closeModal() {
      setModal(false);
    }

    function toggleWeatherMode(e: ChangeEvent<HTMLInputElement>) {
      if (!canManageDevices) {
        return;
      }
      setWeathermodeSettings(prev =>
        prev
          ? {
              ...prev,
              change_weathermode: true,
              weathermode_on: e.target.checked,
              trigger_on_hourly_rainfall: e.target.checked,
              trigger_on_cumulative_rainfall: e.target.checked,
            }
          : prev,
      );
    }

    function toggleTriggerOnHourlyRainfall(e: ChangeEvent<HTMLInputElement>) {
      if (!canManageDevices) {
        return;
      }

      setWeathermodeSettings(prev =>
        prev
          ? {
              ...prev,
              change_weathermode: true,
              trigger_on_hourly_rainfall: e.target.checked,
            }
          : prev,
      );
    }

    function toggleTriggerOnCumulativeRainfall(
      e: ChangeEvent<HTMLInputElement>,
    ) {
      if (!canManageDevices) {
        return;
      }

      setWeathermodeSettings(prev =>
        prev
          ? {
              ...prev,
              change_weathermode: true,
              trigger_on_cumulative_rainfall: e.target.checked,
            }
          : prev,
      );
    }

    function handleThresholdChange(e: ChangeEvent<HTMLInputElement>) {
      setRainfallThresholdError([
        '',
        { minMillimeters: '', maxMillimeters: '' },
      ]);
      const value = distance.from(e.target.value);
      if (weathermodeSettings === null) {
        return;
      }
      if (!/^[0-9.]+$/.test(e.target.value)) {
        setRainfallThresholdError([
          'Please provide a valid number!',
          { minMillimeters: '', maxMillimeters: '' },
        ]);
        return;
      }
      if (value < 2 || value > 20) {
        setRainfallThresholdError([
          'Not in the allowable range millimeters',
          {
            minMillimeters: distance.from_metric(2).toFixed(1),
            maxMillimeters: distance.from_metric(20).toFixed(1),
          },
        ]);
      }

      setWeathermodeSettings(prev =>
        prev
          ? {
              ...prev,
              rainfall_level_to_warn_mm: value,
              change_weathermode: true,
            }
          : prev,
      );
    }

    function handleCumulativeThresholdChange(e: ChangeEvent<HTMLInputElement>) {
      setCumulativeRainfallThresholdError([
        '',
        { minMillimeters: '', maxMillimeters: '' },
      ]);
      const value = distance.from(e.target.value);
      if (weathermodeSettings === null) {
        return;
      }
      if (!/^[0-9.]+$/.test(e.target.value)) {
        setCumulativeRainfallThresholdError([
          'Please provide a valid number!',
          { minMillimeters: '', maxMillimeters: '' },
        ]);
        return;
      }
      if (value < 2 || value > 200) {
        setCumulativeRainfallThresholdError([
          'Not in the allowable range millimeters',
          {
            minMillimeters: distance.from_metric(2).toFixed(1),
            maxMillimeters: distance.from_metric(200).toFixed(1),
          },
        ]);
      }

      setWeathermodeSettings(prev =>
        prev
          ? {
              ...prev,
              cumulative_rainfall_level_to_warn_mm: value,
              change_weathermode: true,
            }
          : prev,
      );
    }

    async function saveSettings() {
      closeModal();
      if (!weathermodeSettings || !times) {
        return;
      }

      await company_controller.modify_company_weathermode_settings(
        weathermodeSettings.weathermode_on,
        weathermodeSettings.rainfall_level_to_warn_mm,
        weathermodeSettings.cumulative_rainfall_level_to_warn_mm,
        weathermodeSettings.trigger_on_hourly_rainfall,
        weathermodeSettings.trigger_on_cumulative_rainfall,
      );
      company_controller.modify_device_settings(
        null,
        null,
        null,
        null,
        times.weathermode_message_wait_time_s,
        times.weathermode_measurement_sleep_time_s,
      );
    }

    useEffect(() => {
      if (!times) return;

      const totalWeathermodeMeasurements =
        times.weathermode_message_wait_time_s /
        times.weathermode_measurement_sleep_time_s;

      if (totalWeathermodeMeasurements > MAX_MEASUREMENTS) {
        setTotalMeasurementsWarning(
          'Total measurements exceed 100! Devices are restricted to 100 measurents per transmission interval.',
        );
      } else {
        setTotalMeasurementsWarning('');
      }
      if (
        times.weathermode_message_wait_time_s < 720 ||
        times.weathermode_message_wait_time_s > 72 * 60 * 60
      ) {
        setWeathermodeRelayError([
          'Not in the allowable range hours',
          { minHours: '0.2', maxHours: '72' },
        ]);
      } else {
        setWeathermodeRelayError(['', { minHours: '', maxHours: '' }]);
      }
      const maxWaitTime = Math.min(
        720 * 60,
        times.weathermode_message_wait_time_s,
      );
      if (
        times.weathermode_measurement_sleep_time_s < 300 ||
        times.weathermode_measurement_sleep_time_s > maxWaitTime
      ) {
        setWeathermodeReadingError([
          'Not in the allowable range minutes',
          {
            minReadingFrequency: '5.0',
            maxReadingFrequency: (maxWaitTime / 60).toFixed(1),
          },
        ]);
      } else {
        setWeathermodeReadingError([
          '',
          { minReadingFrequency: '', maxReadingFrequency: '' },
        ]);
      }
    }, [times]);

    useEffect(() => {
      setForceUpdate(prev => prev + 1);
    }, [distance]);

    if (!times || !weathermodeSettings) {
      return <></>;
    }

    const isSaveButtonEnabled =
      canManageDevices &&
      (weathermodeSettings?.change_weathermode || times.change_time) &&
      !weathermodeRelayError[0] &&
      !weathermodeReadingError[0] &&
      !rainfallThresholdError[0] &&
      !cumulativeRainfallThresholdError[0] &&
      !totalMeasurementsWarning;

    return (
      <>
        <SettingsCard
          id="weather_mode"
          title={t('Weather Mode Settings')}
          sx={{ overflow: 'unset' }}
        >
          <Stack gap={2} p={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={weathermodeSettings.weathermode_on}
                  disabled={!canManageDevices}
                  onChange={toggleWeatherMode}
                />
              }
              label={<MHMTypography medium>{t('Weather Mode')}</MHMTypography>}
            />

            <DisclaimerText fontSize="small">
              {t(
                "Weather Mode monitors forecast rainfall in each device's location. Each time a device relays data, the system checks the forecasted hourly rainfall for the next 24 hours. The device frequencies are updated to those defined below while the total rainfall is above the cumulative threshold or the maximum hourly rainfall is above the hourly threshold. Weather mode will remain active for 6 hours after rainfall falls below the threshold. These settings ensure that not only battery life is conserved, as less frequent Normal Operating frequencies can be selected, but that there is also sufficient flow of data during higher risk heavy rainfall events to provide network insights.",
              )}
            </DisclaimerText>

            {weathermodeSettings.weathermode_on && (
              <Stack direction={isMobile ? 'column' : 'row'} gap={2}>
                <Stack gap={1.5} flex={1}>
                  <MHMTypography variant="h6" color="primary">
                    {t('Triggers')}
                  </MHMTypography>
                  <Stack direction="row" rowGap={1.5} alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            weathermodeSettings.trigger_on_hourly_rainfall
                          }
                          disabled={!canManageDevices}
                          onChange={toggleTriggerOnHourlyRainfall}
                        />
                      }
                      label={undefined}
                      sx={{ height: 44 }}
                    />
                    {weathermodeSettings.trigger_on_hourly_rainfall ? (
                      <CheckPermission
                        hasPermission={canManageDevices}
                        sx={{ flex: 1 }}
                      >
                        <MHMTextField
                          key={forceUpdate}
                          label={t('Rainfall Threshold (hourly):')}
                          defaultValue={distance
                            .from_metric(
                              weathermodeSettings.rainfall_level_to_warn_mm ||
                                0.0,
                            )
                            .toFixed(1)}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  {t('Millimeters')}
                                </InputAdornment>
                              ),
                            },
                          }}
                          error={Boolean(rainfallThresholdError[0])}
                          helperText={
                            <ErrorText component="span">
                              {t(...rainfallThresholdError)}
                            </ErrorText>
                          }
                          size="small"
                          fullWidth
                          disabled={!canManageDevices}
                          onChange={handleThresholdChange}
                        />
                      </CheckPermission>
                    ) : (
                      <MHMTypography lineHeight="1.3">
                        {t('Hourly Threshold Disabled')}
                      </MHMTypography>
                    )}
                  </Stack>
                  <Stack direction="row" rowGap={1.5} alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            weathermodeSettings.trigger_on_cumulative_rainfall
                          }
                          disabled={!canManageDevices}
                          onChange={toggleTriggerOnCumulativeRainfall}
                        />
                      }
                      label={undefined}
                      sx={{ height: 44 }}
                    />
                    {weathermodeSettings.trigger_on_cumulative_rainfall ? (
                      <CheckPermission
                        hasPermission={canManageDevices}
                        sx={{ flex: 1 }}
                      >
                        <MHMTextField
                          key={forceUpdate}
                          label={t(
                            'Rainfall Threshold (cumulative - 24 hours):',
                          )}
                          defaultValue={distance
                            .from_metric(
                              weathermodeSettings.cumulative_rainfall_level_to_warn_mm ||
                                0.0,
                            )
                            .toFixed(1)}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  {t('Millimeters')}
                                </InputAdornment>
                              ),
                            },
                          }}
                          error={Boolean(cumulativeRainfallThresholdError[0])}
                          helperText={
                            <ErrorText component="span">
                              {t(...cumulativeRainfallThresholdError)}
                            </ErrorText>
                          }
                          size="small"
                          fullWidth
                          disabled={!canManageDevices}
                          onChange={handleCumulativeThresholdChange}
                        />
                      </CheckPermission>
                    ) : (
                      <MHMTypography lineHeight="1.3">
                        {t('Cumulative Threshold Disabled')}
                      </MHMTypography>
                    )}
                  </Stack>
                </Stack>

                <FrequenciesSettings
                  title={t('Weather Mode Frequencies')}
                  messageTimeKey="weathermode_message_wait_time_s"
                  messageTimeUnit="hours"
                  messageTimeError={t(...weathermodeRelayError)}
                  measurementTimeKey="weathermode_measurement_sleep_time_s"
                  measurementTimeUnit="minutes"
                  measurementTimeError={t(...weathermodeReadingError)}
                  totalMeasurementsError={t(totalMeasurementsWarning, {
                    maxMeasurements: String(MAX_MEASUREMENTS),
                  })}
                  sx={{ flex: 1 }}
                />
              </Stack>
            )}

            <DisclaimerText fontSize="small">
              {t(
                "Weather mode settings are applied to all of the devices in the company's fleet. These settings are communicated to the devices the next time they transmit data.",
              )}
            </DisclaimerText>

            {isSaveButtonEnabled && (
              <ErrorText fontSize="small">
                {t('Changes not yet saved.')}
              </ErrorText>
            )}
            <CheckPermission hasPermission={canManageDevices}>
              <MHMButton disabled={!isSaveButtonEnabled} onClick={openModal}>
                {t('Save')}
              </MHMButton>
            </CheckPermission>
          </Stack>
        </SettingsCard>

        <ConfirmSaveModal
          open={modal}
          onCancel={closeModal}
          onConfirm={saveSettings}
          message={t('Are you sure you want to save these settings?')}
        >
          {weathermodeSettings.weathermode_on ? (
            <ChangedFrequency
              title={t('Weather Mode Settings')}
              messageTimeKey="weathermode_message_wait_time_s"
              messageTimeUnit="hours"
              measurementTimeKey="weathermode_measurement_sleep_time_s"
              measurementTimeUnit="minutes"
            />
          ) : null}
        </ConfirmSaveModal>
      </>
    );
  },
);
